import React, { useEffect, useRef, useState } from 'react';
import './ScrollStory.css';

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const CAPTIONS = [
    'Every lap creates a moment.',
    'The checkered flag drops.',
    'The moment becomes a collectible.',
    'Vaulted. Showcased. Yours.'
];

/**
 * Signature homepage scroll moment.
 * mode="scroll" - full-width sticky scrub (standalone)
 * mode="split"  - sticky scrub with car left + children (marketplace) on the right
 * Respects prefers-reduced-motion with a static final frame.
 */
const ScrollStory = ({ mode = 'scroll', children }) => {
    const isSplit = mode === 'split';
    const sectionRef = useRef(null);
    const stageRef = useRef(null);
    const [isStatic, setIsStatic] = useState(false);
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        const mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const mqMobile = window.matchMedia('(max-width: 900px)');
        const apply = () => {
            // Phone: static final frame (no sticky scrub empty space)
            setIsStatic(mqMotion.matches || (isSplit && mqMobile.matches));
        };
        apply();
        const add = (mq, fn) => {
            if (mq.addEventListener) {
                mq.addEventListener('change', fn);
                return () => mq.removeEventListener('change', fn);
            }
            mq.addListener(fn);
            return () => mq.removeListener(fn);
        };
        const cleanMotion = add(mqMotion, apply);
        const cleanMobile = add(mqMobile, apply);
        return () => {
            cleanMotion();
            cleanMobile();
        };
    }, [isSplit]);

    useEffect(() => {
        if (isStatic) {
            setPhase(3);
            const stage = stageRef.current;
            if (stage) {
                stage.style.setProperty('--p1', '1');
                stage.style.setProperty('--flash', '0');
                stage.style.setProperty('--p2', '1');
                stage.style.setProperty('--p3', '1');
            }
            return undefined;
        }

        let raf = null;
        let ticking = false;

        const update = () => {
            ticking = false;
            const section = sectionRef.current;
            const stage = stageRef.current;
            if (!section || !stage) return;

            const headerEl = document.querySelector('.menu-bar');
            const headerH = headerEl
                ? Math.round(headerEl.getBoundingClientRect().height)
                : 56;
            section.style.setProperty('--msv-header-h', `${headerH}px`);

            const rect = section.getBoundingClientRect();
            // Progress while sticky sits below the header (not under it)
            const total = section.offsetHeight - window.innerHeight;
            const p = total > 0 ? clamp01((headerH - rect.top) / total) : 1;

            const p1 = clamp01(p / 0.45);
            const flash = clamp01(1 - Math.abs(p - 0.46) / 0.09);
            const p2 = clamp01((p - 0.48) / 0.22);
            const p3 = clamp01((p - 0.72) / 0.26);

            stage.style.setProperty('--p1', p1);
            stage.style.setProperty('--flash', flash);
            stage.style.setProperty('--p2', p2);
            stage.style.setProperty('--p3', p3);

            setPhase(p3 > 0.2 ? 3 : p2 > 0.2 ? 2 : p1 > 0.62 ? 1 : 0);
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                raf = requestAnimationFrame(update);
            }
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [isStatic]);

    const id = isSplit ? 'Split' : '';

    const stage = (
        <div className="ScrollStory-stage" ref={stageRef}>
            <p className="ScrollStory-eyebrow">From the track to your vault</p>

            <svg
                className="ScrollStory-svg"
                viewBox="0 0 900 480"
                role="img"
                aria-label="A NASCAR stock car crosses the finish line, the moment becomes a hero card, and the card is locked into the vault"
                focusable="false"
            >
                <defs>
                    <linearGradient id={`ssSceneBg${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2d3e4e" />
                        <stop offset="100%" stopColor="#22303d" />
                    </linearGradient>
                    <linearGradient id={`ssCarBody${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3d5266" />
                        <stop offset="100%" stopColor="#1d2731" />
                    </linearGradient>
                    <pattern id={`ssChecker${id}`} width="16" height="16" patternUnits="userSpaceOnUse">
                        <rect width="8" height="8" x="0" y="0" fill="#f4f7fa" />
                        <rect width="8" height="8" x="8" y="8" fill="#f4f7fa" />
                        <rect width="8" height="8" x="8" y="0" fill="#151a20" />
                        <rect width="8" height="8" x="0" y="8" fill="#151a20" />
                    </pattern>
                    <radialGradient id={`ssGlow${id}`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#76d376" stopOpacity="0.55" />
                        <stop offset="100%" stopColor="#76d376" stopOpacity="0" />
                    </radialGradient>
                </defs>

                <ellipse className="ss-glow" cx="450" cy="240" rx="360" ry="220" fill={`url(#ssGlow${id})`} />

                <g className="ss-cardunit">
                    <rect className="ss-scene-bg" x="70" y="40" width="760" height="400" rx="24" fill={`url(#ssSceneBg${id})`} />

                    <line x1="90" y1="352" x2="810" y2="352" stroke="#151a20" strokeWidth="6" />
                    <line
                        className="ss-lane"
                        x1="90"
                        y1="368"
                        x2="810"
                        y2="368"
                        stroke="#5a6b7c"
                        strokeWidth="3"
                        strokeDasharray="28 22"
                    />

                    <g className="ss-gate">
                        <rect x="716" y="120" width="8" height="232" rx="3" fill="#151a20" />
                        <rect x="788" y="120" width="8" height="232" rx="3" fill="#151a20" />
                        <rect x="712" y="104" width="88" height="26" rx="4" fill={`url(#ssChecker${id})`} stroke="#151a20" strokeWidth="2" />
                    </g>

                    <g className="ss-speedlines" stroke="#76d376" strokeLinecap="round">
                        <line x1="40" y1="256" x2="150" y2="256" strokeWidth="4" />
                        <line x1="10" y1="284" x2="160" y2="284" strokeWidth="5" />
                        <line x1="55" y1="312" x2="145" y2="312" strokeWidth="4" />
                        <line x1="25" y1="336" x2="130" y2="336" strokeWidth="3" opacity="0.6" />
                    </g>

                    <g className="ss-car">
                        {/* NASCAR stock car - side profile */}
                        <path d="M118 248 L118 236 L168 232 L168 244 Z" fill="#151a20" />
                        <rect x="116" y="244" width="8" height="28" rx="1" fill="#151a20" />
                        <path
                            d="M112 328
                               L112 286
                               Q114 268 132 262
                               L168 254
                               L188 236
                               Q198 226 220 224
                               L292 224
                               Q312 224 322 236
                               L338 256
                               L368 262
                               Q384 266 386 280
                               L388 318
                               Q388 328 376 328 Z"
                            fill={`url(#ssCarBody${id})`}
                        />
                        <path
                            d="M190 254 L204 228 Q210 222 222 222 L286 222 Q298 222 304 232 L318 254 Z"
                            fill="#1d2731"
                        />
                        <path
                            d="M208 250 L218 230 L280 230 L294 250 Z"
                            fill="#b8c6d3"
                            opacity="0.88"
                        />
                        <path d="M368 318 L392 318 L390 328 L366 328 Z" fill="#151a20" />
                        <path d="M128 292 L372 296" stroke="#ffe066" strokeWidth="6" strokeLinecap="round" />
                        <circle cx="248" cy="286" r="20" fill="#f4f7fa" />
                        <text x="248" y="294" textAnchor="middle" fontSize="22" fill="#151a20" fontFamily="inherit" fontWeight="700">3</text>
                        <ellipse cx="378" cy="288" rx="6" ry="8" fill="#ffe066" opacity="0.85" />
                        <g className="ss-wheel">
                            <circle cx="168" cy="328" r="26" fill="#151a20" />
                            <circle cx="168" cy="328" r="12" fill="none" stroke="#b8c6d3" strokeWidth="3" />
                            <line x1="168" y1="318" x2="168" y2="338" stroke="#b8c6d3" strokeWidth="3" />
                            <line x1="158" y1="328" x2="178" y2="328" stroke="#b8c6d3" strokeWidth="3" />
                        </g>
                        <g className="ss-wheel">
                            <circle cx="338" cy="328" r="26" fill="#151a20" />
                            <circle cx="338" cy="328" r="12" fill="none" stroke="#b8c6d3" strokeWidth="3" />
                            <line x1="338" y1="318" x2="338" y2="338" stroke="#b8c6d3" strokeWidth="3" />
                            <line x1="328" y1="328" x2="348" y2="328" stroke="#b8c6d3" strokeWidth="3" />
                        </g>
                    </g>

                    <rect className="ss-flash" x="70" y="40" width="760" height="400" rx="24" fill="#ffe066" />

                    <rect
                        className="ss-cardframe"
                        x="70"
                        y="40"
                        width="760"
                        height="400"
                        rx="24"
                        fill="none"
                        stroke="#f4f7fa"
                        strokeWidth="7"
                        pathLength="100"
                    />
                    <g className="ss-cardplate">
                        <rect x="330" y="60" width="240" height="36" rx="8" fill="#151a20" opacity="0.85" />
                        <text x="450" y="85" textAnchor="middle" fontSize="20" letterSpacing="3" fill="#ffe066" fontFamily="inherit">
                            HERO CARD № 001
                        </text>
                    </g>
                </g>

                <g className="ss-vault" stroke="#76d376" strokeWidth="8" strokeLinecap="round" fill="none">
                    <path d="M162 132 L162 92 L212 92" pathLength="100" className="ss-bracket" />
                    <path d="M688 92 L738 92 L738 132" pathLength="100" className="ss-bracket" />
                    <path d="M738 348 L738 388 L688 388" pathLength="100" className="ss-bracket" />
                    <path d="M212 388 L162 388 L162 348" pathLength="100" className="ss-bracket" />
                </g>

                <g className="ss-lock">
                    <path
                        className="ss-shackle"
                        d="M432 428 L432 414 Q432 398 450 398 Q468 398 468 414 L468 428"
                        fill="none"
                        stroke="#b8c6d3"
                        strokeWidth="9"
                        strokeLinecap="round"
                    />
                    <rect x="418" y="426" width="64" height="46" rx="10" fill="#151a20" stroke="#76d376" strokeWidth="3" />
                    <circle cx="450" cy="446" r="7" fill="#ffe066" />
                </g>
            </svg>

            <div className="ScrollStory-captions" aria-live="polite">
                {CAPTIONS.map((text, i) => (
                    <p key={text} className={`ScrollStory-caption ${phase === i ? 'is-active' : ''}`}>
                        {text}
                    </p>
                ))}
            </div>

            <div className="ScrollStory-progress" aria-hidden="true">
                <span className="ScrollStory-progress-fill" />
            </div>
        </div>
    );

    return (
        <section
            ref={sectionRef}
            className={`ScrollStory ${isSplit ? 'is-split' : ''} ${isStatic ? 'is-static' : ''}`}
            aria-label="From the track to your vault"
        >
            <div className={`ScrollStory-viewport ${isSplit ? 'is-split-viewport' : ''}`}>
                {isSplit ? (
                    <div className="ScrollStory-split">
                        <div className="ScrollStory-split-visual">{stage}</div>
                        <div className="ScrollStory-split-panel">{children}</div>
                    </div>
                ) : (
                    stage
                )}
            </div>
        </section>
    );
};

export default ScrollStory;
